import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * The theme's delegated on:* events proxy `event.target` to the element
 * carrying the attribute (the option card) — composedPath still exposes the
 * element that was actually clicked. Use this wherever a handler must know
 * whether the interaction originated in a nested control (e.g. the variant
 * dropdown inside a card).
 * @param {Event} event
 * @returns {Element | null}
 */
const realEventTarget = (event) => {
  const origin = event.composedPath?.()[0] ?? event.target;
  return origin instanceof Element ? origin : null;
};

/**
 * @typedef {Object} CompanionVariant
 * @property {number} product_id
 * @property {number} variant_id
 * @property {string} product_title
 * @property {string} short_title
 * @property {number} price
 * @property {number} subscription_price - Per-delivery selling-plan price; equals price when the product has no plan
 * @property {string} image
 * @property {number|null} selling_plan_id
 */

/**
 * @typedef {Object} BundleGroup
 * @property {string} key
 * @property {string} label
 * @property {number} pickCount
 * @property {string[]} productIds - GID strings like "gid://shopify/Product/123"
 */

/**
 * @typedef {Object} BundleConfig
 * @property {string} bundleSlug
 * @property {string} bundleName
 * @property {string} discountType - "percentage" or "fixed_amount"
 * @property {number} discountValue
 * @property {BundleGroup[]} groups
 * @property {number} currentProductId
 */

/**
 * @typedef {Object} KitRefs
 * @property {HTMLImageElement} kitImage
 * @property {HTMLElement} kitName
 * @property {HTMLElement} kitSubtitle
 * @property {HTMLElement} kitPrice
 * @property {HTMLElement} kitComparePrice
 * @property {HTMLButtonElement} kitAtcBtn
 * @property {HTMLElement} kitAtcLabel
 * @property {HTMLDialogElement} swapDialog
 * @property {HTMLElement} [dragHandle]
 * @property {HTMLElement} [selectionCount]
 * @property {HTMLButtonElement} [confirmBtn]
 */

/**
 * @extends {Component<KitRefs>}
 */
class AromelyPdpKit extends Component {
  /** @type {BundleConfig} */
  #bundleConfig = {};

  /** @type {CompanionVariant[]} */
  #allProducts = [];

  /** @type {BundleGroup|null} */
  #baseGroup = null;

  /** @type {BundleGroup|null} */
  #selectionGroup = null;

  /** @type {CompanionVariant[]} */
  #companions = [];

  /** @type {Set<number>} */
  #selectedIndices = new Set();

  /** @type {number} */
  #currentPrice = 0;

  /** @type {number} */
  #currentSubscriptionPrice = 0;

  /** @type {string} */
  #moneyFormat = '';

  /** @type {string} */
  #currency = '';

  /** @type {string} */
  #currentProductTitle = '';

  /** @type {number} */
  #kitSize = 1;

  /** @type {number|undefined} */
  #successTimeout;

  connectedCallback() {
    super.connectedCallback();

    // Parse bundle config
    const configScript = this.querySelector('[data-bundle-config]');
    if (configScript) {
      try {
        this.#bundleConfig = JSON.parse(configScript.textContent || '{}');
      } catch { /* empty */ }
    }

    // Parse all bundle product variants
    const productsScript = this.querySelector('[data-bundle-products]');
    if (productsScript) {
      try {
        this.#allProducts = JSON.parse(productsScript.textContent || '[]');
      } catch { /* empty */ }
    }

    // Auto-detect: which group is the current product in?
    // productIds in groups are GIDs like "gid://shopify/Product/123"
    // currentProductId is numeric (123)
    const currentId = this.#bundleConfig.currentProductId;
    let baseGroup = null;
    let selectionGroup = null;

    for (const group of (this.#bundleConfig.groups || [])) {
      const isBase = group.productIds.some(gid => gid.endsWith('/' + currentId));
      if (isBase) {
        baseGroup = group;
      } else {
        selectionGroup = group;
      }
    }

    this.#baseGroup = baseGroup;
    this.#selectionGroup = selectionGroup;

    // Build companions list: all product variants that belong to the selection group
    if (selectionGroup) {
      const selectionProductIds = new Set(
        selectionGroup.productIds.map(gid => parseInt(gid.split('/').pop(), 10))
      );
      this.#companions = this.#allProducts.filter(p => selectionProductIds.has(p.product_id));
      this.#kitSize = selectionGroup.pickCount;

      // Hide dialog options that are NOT in the selection group and re-index visible ones
      let visibleIndex = 0;
      const options = this.querySelectorAll('.aromely-scent-picker__option');
      for (const opt of options) {
        const productId = parseInt(opt.dataset.productId || '0', 10);
        if (!selectionProductIds.has(productId)) {
          opt.style.display = 'none';
          opt.removeAttribute('data-product-index');
        } else {
          opt.dataset.productIndex = String(visibleIndex);
          visibleIndex++;
        }
      }
    }

    this.#currentPrice = parseInt(this.dataset.currentPrice || '0', 10);
    // NaN check, not || — a subscription price of 0 (e.g. fixed-amount plan
    // adjustment equal to the price) is legitimate and must not fall back.
    const subscriptionSeed = parseInt(this.dataset.currentSubscriptionPrice || '', 10);
    this.#currentSubscriptionPrice = Number.isNaN(subscriptionSeed) ? this.#currentPrice : subscriptionSeed;
    this.#moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.#currency = this.dataset.currency || 'USD';

    // Extract current product short title from the subtitle
    const subtitle = this.refs.kitSubtitle?.textContent?.trim() || '';
    const plusIndex = subtitle.indexOf(' + ');
    this.#currentProductTitle = plusIndex > -1 ? subtitle.substring(0, plusIndex) : subtitle;

    // Pre-select first N companions
    for (let i = 0; i < this.#kitSize && i < this.#companions.length; i++) {
      this.#selectedIndices.add(i);
    }

    // Listen for variant changes from parent aromely-pdp component
    const pdpComponent = this.closest('aromely-pdp');
    if (pdpComponent) {
      pdpComponent.addEventListener('aromely:variant-change', (e) => {
        const { variantId, price } = e.detail;
        this.#currentPrice = price;
        this.#currentSubscriptionPrice = this.#applySellingPlanAdjustment(price);
        this.dataset.currentVariantId = variantId.toString();
        this.dataset.currentPrice = price.toString();
        this.#updateDisplay();
      });
    }

    this.#initVariantDropdowns();

    // Hydrate the kit summary from JS state. Liquid renders sensible defaults
    // for first paint, but the canonical truth is #companions[#selectedIndices]
    // — overwriting here makes sure the displayed subtitle and price always
    // reflect the real pickCount × companion sum, even if the metafield JSON
    // typing surprised the Liquid pricing loop.
    this.#updateDisplay();
  }

  /** @type {boolean} */
  #dragInitialized = false;

  openSwapDialog() {
    this.#syncDialogSelection();
    const dialog = this.refs.swapDialog;
    if (!dialog) return;

    dialog.showModal();
    document.body.style.overflow = 'hidden';

    // Close on backdrop click
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) this.closeSwapDialog();
    }, { once: true });

    if (!this.#dragInitialized) {
      this.#initDragToClose();
      this.#dragInitialized = true;
    }
  }

  closeSwapDialog() {
    const dialog = this.refs.swapDialog;
    if (!dialog || !dialog.open) return;

    // Trigger the close animation via a data attribute so CSS can pick the
    // right exit per breakpoint (slide-down on mobile, fade on desktop —
    // mobile slides because the dialog is a bottom sheet; on desktop the
    // dialog is centered with translate(-50%, -50%) and we mustn't clobber it).
    dialog.setAttribute('data-closing', '');

    setTimeout(() => {
      dialog.close();
      dialog.removeAttribute('data-closing');
      document.body.style.overflow = '';
    }, 250);
  }

  /**
   * Toggle a companion in/out of the selection (multi-select mode) or swap (single mode).
   * @param {Event} event
   */
  toggleCompanion(event) {
    // Don't toggle the companion line when the click originated inside the
    // variant dropdown. The theme's delegated routing runs document-capture
    // (stopPropagation can't block it) AND proxies event.target to the card,
    // so the check must use the real composedPath origin.
    if (realEventTarget(event)?.closest('[data-aromely-dropdown]')) return;

    const btn = /** @type {HTMLElement} */ (event.target)?.closest('[data-product-index]');
    if (!btn) return;

    const index = parseInt(btn.dataset.productIndex || '0', 10);

    if (this.#kitSize === 1) {
      // Single select: swap and close
      this.#selectedIndices.clear();
      this.#selectedIndices.add(index);
      this.#syncDialogSelection();
      this.#updateDisplay();
      this.closeSwapDialog();
      return;
    }

    // Multi-select toggle
    if (this.#selectedIndices.has(index)) {
      // Deselect tapped item
      this.#selectedIndices.delete(index);
      btn.removeAttribute('data-selected');
    } else {
      // At capacity: deselect the oldest to make room
      if (this.#selectedIndices.size >= this.#kitSize) {
        const oldest = this.#selectedIndices.values().next().value;
        this.#selectedIndices.delete(oldest);
        this.querySelector(`.aromely-scent-picker__option[data-product-index="${oldest}"]`)
          ?.removeAttribute('data-selected');
      }
      this.#selectedIndices.add(index);
      btn.setAttribute('data-selected', '');
    }

    this.#updateDialogState();
  }

  confirmSelection() {
    this.#updateDisplay();
    this.closeSwapDialog();
  }

  /**
   * Keyboard activation for the option div (Enter/Space). The option is a div
   * (not a button) so it can host the variant dropdown — but it must still be
   * keyboard-accessible.
   * @param {KeyboardEvent} event
   */
  handleOptionKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // Don't hijack keys typed inside the dropdown panel (real origin — see realEventTarget).
    if (realEventTarget(event)?.closest('[data-aromely-dropdown]')) return;
    event.preventDefault();
    this.toggleCompanion(event);
  }

  /**
   * Wires up every variant dropdown rendered inside the swap dialog.
   * Replicates the open/close + outside-click + Escape behavior from the
   * aromely-category-filters sort dropdown so the UX matches.
   */
  #initVariantDropdowns() {
    const dropdowns = this.querySelectorAll('[data-aromely-dropdown]');
    if (dropdowns.length === 0) return;

    /** @type {HTMLElement[]} */
    const allPanels = [];

    for (const dropdown of dropdowns) {
      if (!(dropdown instanceof HTMLElement)) continue;
      const trigger = dropdown.querySelector('[data-dropdown-trigger]');
      const panel = dropdown.querySelector('[data-dropdown-panel]');
      const chevron = dropdown.querySelector('[data-dropdown-chevron]');
      const label = dropdown.querySelector('[data-dropdown-label]');
      const options = dropdown.querySelectorAll('[data-dropdown-option]');
      if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) continue;

      allPanels.push(panel);

      const open = () => {
        // Close any sibling dropdowns first
        for (const other of allPanels) {
          if (other !== panel) this.#closeDropdownPanel(other);
        }
        panel.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        if (chevron instanceof HTMLElement) chevron.style.transform = 'rotate(180deg)';
      };

      // Trigger toggles the panel — stop propagation so the parent option div
      // doesn't also fire toggleCompanion.
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = panel.getAttribute('aria-hidden') === 'false';
        if (isOpen) {
          this.#closeDropdownPanel(panel);
        } else {
          open();
        }
      });

      // Option click → update companion variant.
      for (const option of options) {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          if (!(option instanceof HTMLElement)) return;

          const newVariantId = parseInt(option.dataset.dropdownOption || '0', 10);
          const newPrice = parseInt(option.dataset.dropdownOptionPrice || '0', 10);
          const newComparePrice = parseInt(option.dataset.dropdownOptionComparePrice || '0', 10);
          // Per-variant subscription price, pre-computed in Liquid from the
          // product's first selling plan adjustment. Falls back to base price
          // when the product has no selling plan (the attribute is absent).
          // NaN check, not || — a legitimate 0 must not fall back to base.
          const rawSubscriptionPrice = parseInt(option.dataset.dropdownOptionSubscriptionPrice || '', 10);
          const newSubscriptionPrice = Number.isNaN(rawSubscriptionPrice) ? newPrice : rawSubscriptionPrice;
          const newLabel = option.dataset.dropdownOptionLabel || '';

          // Stack the kit bundle discount on top of the subscription price
          // for the main display. Only percentage discounts can be fairly
          // split per-item — a fixed-amount discount applies to the bundle
          // total, so for fixed_amount we leave the subscription price as
          // the main display and fall back to the base variant price as
          // the compare-at.
          const { discountType, discountValue } = this.#bundleConfig;
          let newFinalPrice = newSubscriptionPrice;
          if (discountType === 'percentage' && discountValue > 0) {
            const kitReduce = Math.round(newSubscriptionPrice * discountValue / 100);
            newFinalPrice = newSubscriptionPrice - kitReduce;
          }

          // Pick the compare-at value in the same priority order the Liquid
          // initial render uses:
          //   1. Kit discount stacked on subscription → subscription price
          //   2. Subscription-only discount → base variant price
          //   3. Variant is natively on sale → variant compare_at_price
          //   4. Nothing → hide
          let compareDisplay = 0;
          if (newFinalPrice < newSubscriptionPrice) {
            compareDisplay = newSubscriptionPrice;
          } else if (newSubscriptionPrice < newPrice) {
            compareDisplay = newPrice;
          } else if (newComparePrice > newPrice) {
            compareDisplay = newComparePrice;
          }

          // Update the trigger label
          if (label instanceof HTMLElement) label.textContent = newLabel;

          // Update aria-selected + background on every option in this dropdown.
          // The checkmark visibility is driven by CSS via aria-selected.
          for (const opt of options) {
            if (!(opt instanceof HTMLElement)) continue;
            const isSelected = opt === option;
            opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            opt.style.backgroundColor = isSelected ? '#e7e5e4' : '';
          }

          // Update the underlying companion in #companions so the kit price &
          // cart payload pick up the new variant. The dropdown lives inside an
          // option element with data-product-index — that's our companion key.
          const optionEl = option.closest('[data-product-index]');
          if (optionEl instanceof HTMLElement) {
            const idx = parseInt(optionEl.dataset.productIndex || '0', 10);
            const companion = this.#companions[idx];
            if (companion) {
              companion.variant_id = newVariantId;
              companion.price = newPrice;
              companion.subscription_price = newSubscriptionPrice;
            }
            // Reflect on the option's data attr too (kept in sync just in case)
            optionEl.dataset.variantId = String(newVariantId);
            // Main price = subscription price with the kit discount stacked
            // on top (when it's a percentage). Compare-at is chosen by the
            // priority ladder above.
            const priceEl = optionEl.querySelector('[data-companion-price]');
            if (priceEl instanceof HTMLElement) {
              priceEl.textContent = formatMoney(newFinalPrice, this.#moneyFormat, this.#currency);
            }
            const compareEl = optionEl.querySelector('[data-companion-compare-price]');
            if (compareEl instanceof HTMLElement) {
              if (compareDisplay > 0) {
                compareEl.textContent = formatMoney(compareDisplay, this.#moneyFormat, this.#currency);
                compareEl.classList.remove('hidden');
              } else {
                compareEl.textContent = '';
                compareEl.classList.add('hidden');
              }
            }
            // Update the variant title shown next to the subtitle
            const variantLabelEl = optionEl.querySelector('[data-companion-variant-label]');
            if (variantLabelEl instanceof HTMLElement) {
              variantLabelEl.textContent = newLabel;
            }
          }

          this.#closeDropdownPanel(panel);
          // Refresh the kit summary (combined price, savings) if this companion is selected.
          this.#updateDisplay();
        });
      }
    }

    // Outside-click + Escape — single delegated handler shared across all dropdowns.
    document.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      for (const panel of allPanels) {
        if (panel.getAttribute('aria-hidden') === 'false') {
          const dropdown = panel.closest('[data-aromely-dropdown]');
          if (dropdown && !dropdown.contains(target)) this.#closeDropdownPanel(panel);
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      for (const panel of allPanels) {
        if (panel.getAttribute('aria-hidden') === 'false') this.#closeDropdownPanel(panel);
      }
    });
  }

  /**
   * @param {HTMLElement} panel
   */
  #closeDropdownPanel(panel) {
    panel.setAttribute('aria-hidden', 'true');
    const dropdown = panel.closest('[data-aromely-dropdown]');
    const trigger = dropdown?.querySelector('[data-dropdown-trigger]');
    const chevron = dropdown?.querySelector('[data-dropdown-chevron]');
    if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', 'false');
    if (chevron instanceof HTMLElement) chevron.style.transform = '';
  }

  #updateDialogState() {
    const count = this.#selectedIndices.size;

    // Update counter
    if (this.refs.selectionCount) {
      this.refs.selectionCount.textContent = `${count} of ${this.#kitSize} selected`;
    }

    // Enable/disable confirm button
    if (this.refs.confirmBtn) {
      this.refs.confirmBtn.disabled = count !== this.#kitSize;
    }
  }

  #syncDialogSelection() {
    // If no JS-side selection yet, read from the Liquid-set defaults in the DOM
    if (this.#selectedIndices.size === 0) {
      const preSelected = this.querySelectorAll('.aromely-scent-picker__option[data-selected]');
      for (const opt of preSelected) {
        const idx = parseInt(opt.dataset.productIndex || '0', 10);
        this.#selectedIndices.add(idx);
      }
    }

    const options = this.querySelectorAll('.aromely-scent-picker__option');
    for (const opt of options) {
      const idx = parseInt(opt.dataset.productIndex || '0', 10);
      if (this.#selectedIndices.has(idx)) {
        opt.setAttribute('data-selected', '');
      } else {
        opt.removeAttribute('data-selected');
      }
    }
    this.#updateDialogState();
  }

  handleKitAddToCart() {
    const btn = this.refs.kitAtcBtn;
    if (!btn || btn.disabled || btn.dataset.state === 'loading') return;

    btn.dataset.state = 'loading';
    btn.disabled = true;

    const currentVariantId = parseInt(this.dataset.currentVariantId, 10);
    const currentSellingPlanId = parseInt(this.dataset.currentSellingPlanId || '', 10) || null;
    const bundleSlug = this.#bundleConfig.bundleSlug || this.dataset.bundleSlug || 'kit';
    const bundleGroupId = `${bundleSlug}-${Date.now()}`;

    // Build items: current product + selected companions, all tagged with _bundle_id.
    // If a product has selling plans, attach the default (first/monthly) plan so
    // the kit creates subscription line items at checkout — Shopify accepts
    // selling_plan per-item and silently ignores items that omit it.
    const currentItem = {
      id: currentVariantId,
      quantity: 1,
      properties: { _bundle_id: bundleGroupId }
    };
    if (currentSellingPlanId) currentItem.selling_plan = currentSellingPlanId;
    const items = [currentItem];

    for (const idx of this.#selectedIndices) {
      const companion = this.#companions[idx];
      if (companion) {
        const item = {
          id: companion.variant_id,
          quantity: 1,
          properties: { _bundle_id: bundleGroupId }
        };
        if (companion.selling_plan_id) item.selling_plan = companion.selling_plan_id;
        items.push(item);
      }
    }

    const body = JSON.stringify({ items });

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    })
      .then(res => res.json())
      .then(async response => {
        if (response.status) {
          console.error('Kit add to cart error:', response.description);
          btn.dataset.state = '';
          btn.disabled = false;
          return;
        }

        // Fallback discount auto-apply — temporary until the Cart Transform app ships.
        // Applied BEFORE dispatching CartAddEvent so the cart drawer opens with the
        // final discounted totals in a single render (no full-price → discounted flash).
        const autoApply = this.dataset.kitDiscountAutoApply === 'true';
        const kitCode = (this.dataset.kitDiscountCode || '').trim();
        if (autoApply && kitCode) {
          await this.#applyKitDiscountCode(kitCode);
        }

        // Go straight to checkout instead of opening the cart drawer if enabled.
        // Keep the button in its loading state — navigation takes over the UI.
        if (this.dataset.kitGoToCheckout === 'true') {
          window.location.href = '/checkout';
          return;
        }

        btn.dataset.state = 'success';

        this.dispatchEvent(
          new CartAddEvent(response, this.id || 'kit', {
            source: 'aromely-pdp-kit',
          })
        );

        if (this.#successTimeout) clearTimeout(this.#successTimeout);
        this.#successTimeout = setTimeout(() => {
          btn.dataset.state = '';
          btn.disabled = false;
        }, 1500);
      })
      .catch(err => {
        console.error('Kit add to cart error:', err);
        btn.dataset.state = '';
        btn.disabled = false;
      });
  }

  /**
   * Applies a discount code to the cart without clobbering any codes the shopper
   * already applied manually. Non-fatal on error — the items are already in the cart.
   * @param {string} code
   */
  async #applyKitDiscountCode(code) {
    try {
      const cartRes = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartRes.json();
      const existing = (cart.cart_level_discount_applications || [])
        .map(d => d.title)
        .filter(Boolean);

      if (existing.includes(code)) return;

      const merged = [...existing, code].join(',');
      await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: merged }),
      });
    } catch (err) {
      console.error('Kit discount auto-apply failed:', err);
    }
  }

  /**
   * Applies the main product's first selling plan price adjustment to a base
   * variant price — mirrors the Liquid computation in aromely-pdp-kit.liquid
   * so the JS-hydrated total matches the first paint to the cent. Returns the
   * base price unchanged when the product has no selling plan.
   * @param {number} basePrice
   * @returns {number}
   */
  #applySellingPlanAdjustment(basePrice) {
    if (!this.dataset.currentSellingPlanId) return basePrice;
    const type = this.dataset.currentSellingPlanAdjustType;
    const value = parseFloat(this.dataset.currentSellingPlanAdjustValue || '0');
    if (type === 'percentage' && value > 0) return basePrice - Math.round(basePrice * value / 100);
    if (type === 'fixed_amount' && value > 0) return basePrice - value;
    if (type === 'price' && value > 0) return value;
    return basePrice;
  }

  #updateDisplay() {
    const selected = [...this.#selectedIndices].map(i => this.#companions[i]).filter(Boolean);
    if (selected.length === 0) return;

    // Image and title are static (bundle image/name set by the app) — only update subtitle and price
    const names = selected.map(c => c.short_title || c.product_title);
    if (this.refs.kitSubtitle) this.refs.kitSubtitle.textContent = names.join(' + ');

    // The kit ATC attaches each line's selling plan, so the total is built
    // from subscription prices (base price when a product has no plan).
    let combinedPrice = this.#currentSubscriptionPrice;
    for (const c of selected) combinedPrice += c.subscription_price ?? c.price;

    let kitPrice;
    const { discountType, discountValue } = this.#bundleConfig;
    if (discountType === 'fixed_amount') {
      kitPrice = Math.max(0, combinedPrice - (discountValue * 100)); // discountValue is in dollars, prices in cents
    } else {
      const discountAmount = Math.round(combinedPrice * discountValue / 100);
      kitPrice = combinedPrice - discountAmount;
    }

    if (this.refs.kitPrice) {
      this.refs.kitPrice.textContent = formatMoney(kitPrice, this.#moneyFormat, this.#currency);
    }
    if (this.refs.kitComparePrice) {
      this.refs.kitComparePrice.textContent = formatMoney(combinedPrice, this.#moneyFormat, this.#currency);
    }
    if (this.refs.kitDiscountBadge) {
      if (combinedPrice > kitPrice) {
        const pct = Math.round(((combinedPrice - kitPrice) / combinedPrice) * 100);
        this.refs.kitDiscountBadge.textContent = `-${pct}%`;
        this.refs.kitDiscountBadge.classList.add('inline-flex');
        this.refs.kitDiscountBadge.classList.remove('hidden');
      } else {
        this.refs.kitDiscountBadge.classList.add('hidden');
        this.refs.kitDiscountBadge.classList.remove('inline-flex');
      }
    }
  }

  #initDragToClose() {
    const dialog = this.refs.swapDialog;
    if (!dialog) return;

    const handle = dialog.querySelector('[ref="dragHandle"]');
    if (!handle) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    const onStart = (e) => {
      isDragging = true;
      startY = e.touches[0].clientY;
      currentY = 0;
      dialog.setAttribute('data-dragging', '');
      dialog.style.transition = 'none';
      dialog.style.transform = 'translateY(0)';
    };

    const onMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      currentY = Math.max(0, y - startY);
      dialog.style.transform = `translateY(${currentY}px)`;
    };

    const onEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      dialog.style.transition = 'transform 0.25s cubic-bezier(0.32, 0.72, 0, 1)';
      if (currentY > 80) {
        dialog.style.transform = 'translateY(100%)';
        setTimeout(() => {
          dialog.close();
          dialog.removeAttribute('data-dragging');
          dialog.style.transform = '';
          dialog.style.transition = '';
          document.body.style.overflow = '';
        }, 250);
      } else {
        dialog.style.transform = 'translateY(0)';
        setTimeout(() => {
          dialog.style.transform = '';
          dialog.style.transition = '';
        }, 250);
      }
    };

    handle.addEventListener('touchstart', onStart, { passive: true });
    handle.addEventListener('touchmove', onMove, { passive: false });
    handle.addEventListener('touchend', onEnd);
  }
}

if (!customElements.get('aromely-pdp-kit')) {
  customElements.define('aromely-pdp-kit', AromelyPdpKit);
}
